/* eslint-disable no-nested-ternary */
import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Check, ArrowLeft } from '@saleshandy/icons';
import { Button } from '@saleshandy/design-system';
import { RootState } from '../../../../store/root-reducer';
import { RequestStatus } from '../../../../shared/enums/request-status';
import {
  NewEnrichmentStep,
  EnrichmentType,
  EnrichmentComponent,
} from '../../enums/csv-enrichment.enums';
import { ColumnMapping } from '../../types/csv-enrichment.types';
import {
  setCurrentStep,
  setExportColumns,
  setUploadedFile,
  setColumnMapping,
} from '../../csv-enrichment-slice';
import { Routes } from '../../../../shared/routes';
import SelectTypeStep from './select-type-step';
import UploadMapStep from './upload-map-step';
import ChooseComponentsStep from './choose-components-step';
import {
  accessibleOnClick,
  redirectWithoutRefresh,
} from '../../../../shared/utils';
import { getIsDemoAccount } from '../../../../shared/utils/user-details';
import GlobalRestrictionModal from '../../../home/components/global-restriction-modal';
import {
  OverlayTooltip,
  Placement,
} from '../../../../shared/design-system/components/overlay';
import { createEnrichmentJob } from '../../extra-actions';
import { computeLeadRevealCredits } from '../../../v2-lead/helpers/helper';
import { LeadRevealType } from '../../../v2-lead/types';
import CreditNotAvailableModal from '../../../v2-lead/components/modal/credit-not-available-modal';

const STEPS = [
  {
    number: NewEnrichmentStep.SELECT_TYPE,
    title: 'Select Type',
    subtitle: 'Choose enrichment type',
  },
  {
    number: NewEnrichmentStep.UPLOAD_MAP,
    title: 'Upload & Map',
    subtitle: 'Upload CSV and map fields',
  },
  {
    number: NewEnrichmentStep.CHOOSE_COMPONENTS,
    title: 'Choose Components',
    subtitle: 'Select enrichment data types',
  },
];

const NewEnrichmentPage: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [
    showCreditNotAvailableModal,
    setShowCreditNotAvailableModal,
  ] = useState(false);
  const [
    creditModalRevealType,
    setCreditModalRevealType,
  ] = useState<LeadRevealType | null>(null);
  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );

  const {
    currentStep,
    selectedType,
    uploadedFile,
    columnMapping,
    selectedComponent,
    exportColumns,
    createJobRequestStatus,
  } = useSelector((state: RootState) => state.csvEnrichment);
  const leadFinderCredits = useSelector(
    (state: RootState) => (state.home as any)?.leadFinder?.credits,
  ) as { email?: number; emailphone?: number; company?: number } | undefined;
  const maxCsvEnrichmentRecords = useSelector(
    (state: RootState) => (state.home as any)?.leadFinder?.csvEnrichment,
  ) as { row?: number; column?: number; size?: number } | undefined;

  const leadReveal = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
        'LEAD.REVEAL'
      ],
  );
  const leadRevealBucket1 = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
        'LEAD.REVEAL.BUCKET1'
      ],
  );
  const leadRevealBucket2 = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
        'LEAD.REVEAL.BUCKET2'
      ],
  );
  const isBucket1FrozenFromHome = useSelector(
    (state: RootState) => state?.home?.leadFinder?.isBucket1Frozen,
  );
  const getLeadFinderSubscriptionResponse = useSelector(
    (state: RootState) =>
      state.billingAndSubscription?.getLeadFinderSubscriptionResponse,
  );

  const isBucket1Frozen = useMemo(() => {
    const bucket1 = getLeadFinderSubscriptionResponse?.buckets?.find(
      (b: { type?: string }) => b.type === 'BUCKET1',
    );
    if (bucket1 != null) {
      return bucket1.isFrozen;
    }
    return isBucket1FrozenFromHome;
  }, [getLeadFinderSubscriptionResponse, isBucket1FrozenFromHome]);

  const { availableCredits } = useMemo(
    () =>
      computeLeadRevealCredits(
        leadReveal,
        leadRevealBucket1,
        leadRevealBucket2,
        { isBucket1Frozen },
      ),
    [leadReveal, leadRevealBucket1, leadRevealBucket2, isBucket1Frozen],
  );

  const handleCancel = () => {
    history.push(Routes.CSV_Enrichment);
  };

  const handleBack = () => {
    if (currentStep === NewEnrichmentStep.SELECT_TYPE) {
      history.push(Routes.CSV_Enrichment);
    } else {
      dispatch(setCurrentStep((currentStep - 1) as NewEnrichmentStep));
    }
  };

  const handleUploadMapBackToDropzone = () => {
    // Stay on Step 2, but reset the uploaded file + mapping so dropzone is shown.
    dispatch(setUploadedFile(null));
    dispatch(
      setColumnMapping({
        firstName: null,
        lastName: null,
        companyName: null,
        companyDomain: null,
        linkedinUrl: null,
      }),
    );
  };

  const getBackLabel = () => {
    if (currentStep === NewEnrichmentStep.SELECT_TYPE) return 'Back to List';
    if (currentStep === NewEnrichmentStep.UPLOAD_MAP) {
      return selectedType === EnrichmentType.PEOPLE
        ? 'People enrichment'
        : 'Company enrichment';
    }
    return 'Upload & Map';
  };

  const getStepStatus = (stepNumber: number) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'active';
    return 'upcoming';
  };

  const renderStep = () => {
    switch (currentStep) {
      case NewEnrichmentStep.SELECT_TYPE:
        return <SelectTypeStep />;
      case NewEnrichmentStep.UPLOAD_MAP:
        return <UploadMapStep />;
      case NewEnrichmentStep.CHOOSE_COMPONENTS:
        return <ChooseComponentsStep />;
      default:
        return null;
    }
  };

  const canShowFooter =
    uploadedFile && currentStep === NewEnrichmentStep.UPLOAD_MAP;
  const isAllMapped =
    selectedType === EnrichmentType.COMPANY
      ? !!columnMapping.companyName &&
        !!(columnMapping.companyDomain || columnMapping.linkedinUrl)
      : !!columnMapping.linkedinUrl ||
        (!!columnMapping.firstName &&
          !!columnMapping.lastName &&
          !!(
            columnMapping.companyName ||
            columnMapping.companyDomain ||
            columnMapping.linkedinUrl
          ));

  const handleUploadMapNext = () => {
    const defaultColumns =
      selectedType === EnrichmentType.COMPANY
        ? ([
            'company_name',
            'founded_year',
            'industry',
            'description_enriched',
            'followers_count_linkedin',
            'company_size_range',
          ] as const)
        : ([
            'full_name',
            'emails',
            'job_title',
            'phones',
            'department',
            'location',
            'company_name',
            'company_website',
          ] as const);

    dispatch(setExportColumns([...defaultColumns]));
    dispatch(setCurrentStep(NewEnrichmentStep.CHOOSE_COMPONENTS));
  };

  const rowLimit = maxCsvEnrichmentRecords?.row ?? 10000;
  const columnLimit = maxCsvEnrichmentRecords?.column ?? 100;
  const rowCount = uploadedFile?.rowCount ?? 0;
  const isRowCountEmpty = rowCount === 0;
  const isRowCountExceeded = (uploadedFile?.rowCount ?? 0) > rowLimit;
  const isColumnCountExceeded =
    (uploadedFile?.headers?.length ?? 0) > columnLimit;
  const uploadLimitTooltip = isRowCountEmpty
    ? 'CSV must contain at least 1 data row'
    : isRowCountExceeded
    ? `CSV must not exceed ${rowLimit.toLocaleString()} rows`
    : isColumnCountExceeded
    ? `CSV must not exceed ${columnLimit.toLocaleString()} columns`
    : '';
  const hasUploadValidationError =
    isRowCountEmpty || isRowCountExceeded || isColumnCountExceeded;

  const isSubmitting = createJobRequestStatus === RequestStatus.Pending;
  const canShowChooseComponentsFooter =
    currentStep === NewEnrichmentStep.CHOOSE_COMPONENTS;
  const creditsPerRecord =
    selectedComponent === EnrichmentComponent.EMAIL
      ? selectedType === EnrichmentType.COMPANY
        ? leadFinderCredits?.company ?? 0.1
        : leadFinderCredits?.email ?? 1
      : selectedComponent === EnrichmentComponent.PHONE_EMAIL
      ? leadFinderCredits?.emailphone ?? 7
      : 0;
  const totalCredits = rowCount * creditsPerRecord;

  const getRevealTypeForStart = (): LeadRevealType => {
    if (selectedType === EnrichmentType.COMPANY) {
      return LeadRevealType.Company;
    }
    if (selectedComponent === EnrichmentComponent.PHONE_EMAIL) {
      return LeadRevealType.EmailPhone;
    }
    return LeadRevealType.Email;
  };

  const handleCreditModalHide = () => {
    setShowCreditNotAvailableModal(false);
    setCreditModalRevealType(null);
  };

  const handleCreditModalAddCredits = () => {
    redirectWithoutRefresh(Routes.Setting_Billing_Add_Credits);
    handleCreditModalHide();
  };

  const getFilteredColumnMapping = (): ColumnMapping => {
    if (selectedType === EnrichmentType.COMPANY) {
      return {
        firstName: null,
        lastName: null,
        companyName: columnMapping.companyName,
        linkedinUrl: columnMapping.linkedinUrl,
        companyDomain: columnMapping.companyDomain,
      };
    }
    return columnMapping;
  };

  const handleStartEnrichment = () => {
    if (!selectedComponent || !selectedType || !uploadedFile) return;

    if (getIsDemoAccount()) {
      setShowGlobalRestrictionModal(true);
      return;
    }

    if (totalCredits > availableCredits) {
      setCreditModalRevealType(getRevealTypeForStart());
      setShowCreditNotAvailableModal(true);
      return;
    }

    dispatch(
      createEnrichmentJob({
        fileName: uploadedFile.name,
        revealType: selectedComponent,
        columnMapping: getFilteredColumnMapping(),
        component: selectedType,
        exportColumns,
        totalRecords: uploadedFile.rowCount,
      }),
    );
  };

  return (
    <div className="bs-csv-enrichment__new-page">
      <div className="bs-csv-enrichment__new-page-header">
        <div>
          <div
            className="bs-csv-enrichment__back-btn"
            {...accessibleOnClick(handleBack)}
          >
            <ArrowLeft width={16} height={16} />
            <span>{getBackLabel()}</span>
          </div>
        </div>

        <div className="bs-csv-enrichment__stepper">
          {STEPS.map((step, index) => {
            const status = getStepStatus(step.number);
            return (
              <React.Fragment key={step.number}>
                <div className="bs-csv-enrichment__step">
                  <div
                    className={`bs-csv-enrichment__step-circle bs-csv-enrichment__step-circle--${status}`}
                  >
                    {status === 'completed' ? (
                      <Check width={14} height={14} />
                    ) : (
                      step.number
                    )}
                  </div>
                  <div className="bs-csv-enrichment__step-text">
                    <span className="title blue-txt-15">{step.title}</span>
                    {/* <span className="regular-1 gray-txt-11">
                      {step.subtitle}
                    </span> */}
                  </div>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`bs-csv-enrichment__step-line ${
                      index < currentStep - 1
                        ? 'bs-csv-enrichment__step-line--completed'
                        : ''
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="d-flex justify-content-end align-items-center flex-1">
          {currentStep !== NewEnrichmentStep.SELECT_TYPE && (
            <button
              type="button"
              className="bs-csv-enrichment__cancel-link"
              onClick={handleCancel}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div
        className={`bs-csv-enrichment__step-content ${
          currentStep === NewEnrichmentStep.SELECT_TYPE
            ? 'bs-csv-enrichment__step-content--no-max-width'
            : ''
        } ${
          currentStep === NewEnrichmentStep.UPLOAD_MAP && !uploadedFile
            ? 'bs-csv-enrichment__step-content--dropzone-padding'
            : ''
        }`}
      >
        {renderStep()}
      </div>

      {canShowFooter && (
        <div className="bs-csv-enrichment__step-actions">
          <Button
            variant="secondary"
            onClick={handleUploadMapBackToDropzone}
            className="back-btn"
          >
            Back
          </Button>
          {hasUploadValidationError ? (
            <OverlayTooltip placement={Placement.Top} text={uploadLimitTooltip}>
              <Button
                disabled
                onClick={handleUploadMapNext}
                className="next-btn"
              >
                Next
              </Button>
            </OverlayTooltip>
          ) : (
            <Button
              disabled={!isAllMapped || hasUploadValidationError}
              onClick={handleUploadMapNext}
              className="next-btn"
            >
              Next
            </Button>
          )}
        </div>
      )}

      {canShowChooseComponentsFooter && (
        <div className="bs-csv-enrichment__step-actions bs-csv-enrichment__step-actions--three-col">
          <Button
            variant="secondary"
            onClick={handleBack}
            disabled={isSubmitting}
            className="back-btn"
          >
            Back
          </Button>
          <div className="credit-enrichment-footer">
            {creditsPerRecord > 0 && (
              <div className="bs-csv-enrichment__credit-summary">
                <div>
                  <span className="rows">{rowCount.toLocaleString()} rows</span>{' '}
                  <span className="credits">
                    &times; {creditsPerRecord}{' '}
                    {creditsPerRecord === 1 ? 'credit' : 'credits'} ={' '}
                  </span>
                </div>
                <span className="semibold-2 blue-txt-15">
                  {totalCredits.toLocaleString()} total credits
                </span>
              </div>
            )}
            <Button
              disabled={!selectedComponent || isSubmitting}
              onClick={handleStartEnrichment}
              className="next-btn"
            >
              {isSubmitting ? 'Starting...' : 'Start Enrichment'}
            </Button>
          </div>
        </div>
      )}

      {showCreditNotAvailableModal && creditModalRevealType != null && (
        <CreditNotAvailableModal
          show={showCreditNotAvailableModal}
          onHide={handleCreditModalHide}
          onAddCredits={handleCreditModalAddCredits}
          revealType={creditModalRevealType}
        />
      )}

      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title="Demo account restrictions"
        content="This is a demo account with sample data. Sign up to run CSV enrichment on your own files."
      />
    </div>
  );
};

export default NewEnrichmentPage;
